import React, { useState, useEffect, useCallback } from 'react'
import { 
  Upload, FolderPlus, Grid, List, Search, X, 
  Download, Share2, Trash2, Edit, Eye, Check
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { 
  getFiles, 
  uploadFile, 
  createFolder, 
  deleteItem, 
  renameItem, 
  getSharedLinks, 
  createShareLink, 
  deleteShareLink 
} from '../services/fileService'
import FileGrid from '../components/FileGrid'
import FileList from '../components/FileList'
import PathBreadcrumb from '../components/PathBreadcrumb'
import ShareLinkTable from '../components/ShareLinkTable'
import FileUploader from '../components/FileUploader'
import { useToast } from '../hooks/useToast'

interface FileItem {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  mimeType?: string
  modifiedAt: string
  path: string
}

interface ShareLink {
  id: string
  hash: string
  itemId: string
  itemName: string
  itemType: 'file' | 'folder'
  createdAt: string
  views: number
  downloads: number
}

interface PortalPageProps {
  activeTab: 'files' | 'shared'
}

const PortalPage: React.FC<PortalPageProps> = ({ activeTab: initialActiveTab }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [files, setFiles] = useState<FileItem[]>([])
  const [sharedLinks, setSharedLinks] = useState<ShareLink[]>([])
  const [currentPath, setCurrentPath] = useState<string>('/')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return localStorage.getItem('viewMode') as 'grid' | 'list' || 'grid'
  })
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [uploadSpeed, setUploadSpeed] = useState<number>(0)
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false)
  const [newFolderName, setNewFolderName] = useState<string>('')
  const [isRenaming, setIsRenaming] = useState<boolean>(false)
  const [itemToRename, setItemToRename] = useState<FileItem | null>(null)
  const [newName, setNewName] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'files' | 'shared'>(initialActiveTab)
  const [loading, setLoading] = useState<boolean>(true)
  const [sharingItemId, setSharingItemId] = useState<string | undefined>(undefined)

  useEffect(() => {
    setActiveTab(initialActiveTab)
  }, [initialActiveTab])

  useEffect(() => {
    if (user) {
      loadFiles()
      loadSharedLinks()
    }
  }, [user, currentPath])

  useEffect(() => {
    localStorage.setItem('viewMode', viewMode)
  }, [viewMode])

  // Global keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // If Escape key is pressed and we're in the files tab
      if (e.key === 'Escape' && activeTab === 'files') {
        // Clear selection if any items are selected
        if (selectedItems.length > 0) {
          setSelectedItems([])
        }
      }
    }

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown as any)

    // Clean up
    return () => {
      document.removeEventListener('keydown', handleKeyDown as any)
    }
  }, [activeTab, selectedItems])

  const loadFiles = async () => {
    try {
      setLoading(true)
      const filesData = await getFiles(currentPath)
      setFiles(filesData)
    } catch (error) {
      console.error('Failed to load files:', error)
      showToast('Failed to load files', 'error')
    } finally {
      setLoading(false)
    }
  }

  const loadSharedLinks = async () => {
    try {
      setLoading(true)
      const links = await getSharedLinks()
      setSharedLinks(links)
    } catch (error) {
      console.error('Failed to load shared links:', error)
      showToast('Failed to load shared links', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (files: File[]) => {
    if (!files || files.length === 0) return

    setIsUploading(true)
    setUploadProgress(0)
    
    const totalSize = files.reduce((total, file) => total + file.size, 0)
    let uploadedSize = 0
    const startTime = Date.now()
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        // Mock upload progress updates
        const updateInterval = setInterval(() => {
          const elapsedTime = (Date.now() - startTime) / 1000 // in seconds
          const currentSpeed = uploadedSize / elapsedTime // bytes per second
          
          // Estimate remaining time
          const remainingSize = totalSize - uploadedSize
          const remainingTime = currentSpeed > 0 ? remainingSize / currentSpeed : 0
          
          setUploadSpeed(currentSpeed)
          setTimeRemaining(remainingTime)
        }, 1000)
        
        await uploadFile(file, currentPath, (progress) => {
          const fileProgress = (progress / 100) * file.size
          const newUploadedSize = uploadedSize + fileProgress
          const totalProgress = (newUploadedSize / totalSize) * 100
          
          setUploadProgress(totalProgress)
          
          if (progress === 100) {
            uploadedSize += file.size
          }
        })
        
        clearInterval(updateInterval)
      }
      
      loadFiles()
      showToast(`${files.length} ${files.length === 1 ? 'file' : 'files'} uploaded successfully`, 'success')
    } catch (error) {
      console.error('Upload failed:', error)
      showToast('Upload failed', 'error')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      setUploadSpeed(0)
      setTimeRemaining(0)
    }
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return

    try {
      await createFolder(newFolderName, currentPath)
      showToast(`Folder "${newFolderName}" created successfully`, 'success')
      setNewFolderName('')
      setIsCreatingFolder(false)
      loadFiles()
    } catch (error) {
      console.error('Failed to create folder:', error)
      showToast('Failed to create folder', 'error')
    }
  }

  const handleDeleteItems = async () => {
    if (selectedItems.length === 0) return

    try {
      for (const itemId of selectedItems) {
        await deleteItem(itemId)
      }
      showToast(`${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} moved to trash`, 'success')
      setSelectedItems([]) // Reset selection after deletion
      loadFiles()
    } catch (error) {
      console.error('Failed to delete items:', error)
      showToast('Failed to delete items', 'error')
    }
  }

  const handleDeleteItem = async (item: FileItem) => {
    try {
      await deleteItem(item.id)
      showToast(`"${item.name}" moved to trash`, 'success')
      // If the deleted item was selected, remove it from selection
      if (selectedItems.includes(item.id)) {
        setSelectedItems(prev => prev.filter(id => id !== item.id))
      }
      loadFiles()
    } catch (error) {
      console.error('Failed to delete item:', error)
      showToast('Failed to delete item', 'error')
    }
  }

  const handleRenameItem = async () => {
    if (!itemToRename || !newName.trim()) return

    try {
      await renameItem(itemToRename.id, newName)
      showToast(`Renamed to "${newName}" successfully`, 'success')
      setIsRenaming(false)
      setItemToRename(null)
      setNewName('')
      loadFiles()
    } catch (error) {
      console.error('Failed to rename item:', error)
      showToast('Failed to rename item', 'error')
    }
  }

  const handleCreateShareLink = async (item: FileItem) => {
    try {
      setSharingItemId(item.id)
      const newLink = await createShareLink(item.id)
      
      // Copy the share link to clipboard
      const linkUrl = `${window.location.origin}/${newLink.hash}`
      navigator.clipboard.writeText(linkUrl)
        .then(() => {
          showToast(`Share link created for "${item.name}" and copied to clipboard`, 'success')
        })
        .catch((err) => {
          console.error('Failed to copy link to clipboard:', err)
          showToast(`Share link created for "${item.name}"`, 'success')
        })
      
      // Update the local sharedLinks state with the new link
      setSharedLinks(prev => [newLink, ...prev])
    } catch (error) {
      console.error('Failed to create share link:', error)
      showToast('Failed to create share link', 'error')
    } finally {
      setSharingItemId(undefined)
    }
  }

  const handleDeleteShareLink = async (linkId: string) => {
    try {
      await deleteShareLink(linkId)
      showToast('Share link deleted successfully', 'success')
      // Update local state instead of reloading
      setSharedLinks(prev => prev.filter(link => link.id !== linkId))
    } catch (error) {
      console.error('Failed to delete share link:', error)
      showToast('Failed to delete share link', 'error')
    }
  }

  const handleItemClick = (item: FileItem) => {
    if (item.type === 'folder') {
      setCurrentPath(item.path)
    }
  }

  const handleNavigateUp = () => {
    if (currentPath === '/') return
    
    const pathParts = currentPath.split('/')
    pathParts.pop()
    const newPath = pathParts.join('/') || '/'
    setCurrentPath(newPath)
  }

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    )
  }

  const handleSelectAll = () => {
    if (selectedItems.length === filteredFiles.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(filteredFiles.map(file => file.id))
    }
  }

  const clearSelection = () => {
    setSelectedItems([])
  }

  const filteredFiles = searchQuery
    ? files.filter(file => 
        file.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : files

  const handleCopyLink = (hash: string) => {
    const linkUrl = `${window.location.origin}/${hash}`
    navigator.clipboard.writeText(linkUrl)
    showToast('Link copied to clipboard', 'success')
  }

  const handleViewLink = (link: ShareLink) => {
    window.open(`/${link.hash}`, '_blank')
  }

  // Handle Enter key press for new folder creation
  const handleNewFolderKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCreateFolder()
    }
  }

  // Handle Enter key press for rename
  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleRenameItem()
    }
  }

  return (
    <div className="h-full overflow-auto p-6 pb-16">
      {activeTab === 'files' && (
        <>
          <div className="mb-6">
            <PathBreadcrumb 
              currentPath={currentPath}
              onNavigate={setCurrentPath}
              onNavigateUp={handleNavigateUp}
            />
            
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center space-x-2">
                <button
                  className="btn btn-primary flex items-center"
                  onClick={() => setIsCreatingFolder(true)}
                >
                  <FolderPlus size={18} className="mr-2" />
                  New Folder
                </button>
                
                {selectedItems.length > 0 && (
                  <>
                    <button
                      className="btn bg-red-500 text-white hover:bg-red-600 flex items-center"
                      onClick={handleDeleteItems}
                    >
                      <Trash2 size={18} className="mr-2" />
                      Delete ({selectedItems.length})
                    </button>
                    <button
                      className="btn btn-outline flex items-center"
                      onClick={clearSelection}
                    >
                      <X size={18} className="mr-2" />
                      Clear Selection
                    </button>
                  </>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search size={18} className="text-[var(--color-accent)]" />
                  </div>
                  <input
                    type="text"
                    className="input pl-10 pr-10"
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      onClick={() => setSearchQuery('')}
                    >
                      <X size={18} className="text-[var(--color-accent)]" />
                    </button>
                  )}
                </div>
                
                <button
                  className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-[var(--color-secondary)] text-white' : 'bg-[var(--color-background)] text-[var(--color-primary)]'}`}
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  <Grid size={20} />
                </button>
                
                <button
                  className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-[var(--color-secondary)] text-white' : 'bg-[var(--color-background)] text-[var(--color-primary)]'}`}
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  <List size={20} />
                </button>
              </div>
            </div>
            
            <FileUploader 
              onFilesSelected={handleFileUpload}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              uploadSpeed={uploadSpeed}
              timeRemaining={timeRemaining}
              currentPath={currentPath}
            />
            
            {isCreatingFolder && (
              <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-4 mb-4">
                <h3 className="text-lg font-semibold mb-2">Create New Folder</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    className="input flex-grow"
                    placeholder="Folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={handleNewFolderKeyDown}
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <button
                      className="btn btn-primary flex items-center"
                      onClick={handleCreateFolder}
                    >
                      <Check size={18} className="mr-2" />
                      Create
                    </button>
                    <button
                      className="btn btn-outline flex items-center"
                      onClick={() => {
                        setIsCreatingFolder(false)
                        setNewFolderName('')
                      }}
                    >
                      <X size={18} className="mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {isRenaming && itemToRename && (
              <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-4 mb-4">
                <h3 className="text-lg font-semibold mb-2">Rename Item</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    className="input flex-grow"
                    placeholder="New name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={handleRenameKeyDown}
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <button
                      className="btn btn-primary flex items-center"
                      onClick={handleRenameItem}
                    >
                      <Check size={18} className="mr-2" />
                      Rename
                    </button>
                    <button
                      className="btn btn-outline flex items-center"
                      onClick={() => {
                        setIsRenaming(false)
                        setItemToRename(null)
                        setNewName('')
                      }}
                    >
                      <X size={18} className="mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {viewMode === 'grid' ? (
              <FileGrid
                files={filteredFiles}
                selectedItems={selectedItems}
                onItemClick={handleItemClick}
                onItemSelect={toggleItemSelection}
                onDeleteItem={handleDeleteItem}
                onRenameItem={(item) => {
                  setItemToRename(item)
                  setNewName(item.name)
                  setIsRenaming(true)
                }}
                onCreateShareLink={handleCreateShareLink}
                sharingItemId={sharingItemId}
                loading={loading}
              />
            ) : (
              <FileList
                files={filteredFiles}
                selectedItems={selectedItems}
                onSelectAll={handleSelectAll}
                onItemClick={handleItemClick}
                onItemSelect={toggleItemSelection}
                onDeleteItem={handleDeleteItem}
                onRenameItem={(item) => {
                  setItemToRename(item)
                  setNewName(item.name)
                  setIsRenaming(true)
                }}
                onCreateShareLink={handleCreateShareLink}
                sharingItemId={sharingItemId}
                loading={loading}
              />
            )}
          </div>
        </>
      )}
      
      {activeTab === 'shared' && (
        <ShareLinkTable
          links={sharedLinks}
          onDeleteLink={handleDeleteShareLink}
          onCopyLink={handleCopyLink}
          onViewLink={handleViewLink}
          loading={loading}
        />
      )}
    </div>
  )
}

export default PortalPage